"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

/**
 * Sized, stateful avatar parts for entity visuals — the size scale, the
 * suspended ("muted") greyscale, and the invited dashed-ring treatments are
 * one visual system shared by single avatars, avatar stacks (EntityGroup),
 * and count chips. Consumers supply content (initials, glyphs, images);
 * nothing here knows what an entity is.
 */

export type EntityAvatarSize = "2xs" | "xs" | "sm" | "default" | "lg" | "xl";

// Single source of truth for avatar diameter (px) per tier, mirroring the
// `size-N` classes in entityAvatarVariants. Consumers sizing a sibling box to
// match an avatar must read this rather than re-encoding the literals.
export const AVATAR_SIZE_PX: Record<EntityAvatarSize, number> = {
  "2xs": 16,
  xs: 20,
  sm: 24,
  default: 32,
  lg: 48,
  xl: 64,
};

export const entityAvatarVariants = cva("relative flex shrink-0 select-none", {
  variants: {
    size: {
      "2xs": "size-4",
      xs: "size-5",
      sm: "size-6",
      default: "size-8",
      lg: "size-12",
      xl: "size-16",
    },
    shape: {
      circle: "rounded-full",
      square: "rounded-md",
    },
    invited: {
      true: "border border-dashed border-muted-foreground/60",
    },
  },
  defaultVariants: {
    size: "default",
    shape: "circle",
  },
});

export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  return (parts[0]?.[0] ?? "?").toUpperCase();
}

/** Live presence states an indicator can show. */
export type EntityAvatarPresenceStatus = "online" | "away" | "offline";

const PRESENCE_LABELS: Record<EntityAvatarPresenceStatus, string> = {
  online: "Online",
  away: "Away",
  offline: "Offline",
};

const entityAvatarPresenceDotVariants = cva(
  "pointer-events-none absolute z-10 rounded-full border-background",
  {
    variants: {
      status: {
        online: "bg-success",
        away: "bg-warning",
        // Solid (no alpha): the dot overlaps the avatar's edge, so a
        // translucent fill would show the circle through it and shift hue
        // per avatar color. muted-foreground is the neutral grey ramp.
        offline: "bg-muted-foreground",
      },
      // Dot diameter per avatar size — big enough to read at a glance,
      // small enough not to swallow the circle. Ring thickness scales with
      // it (1px under 24px avatars, 2px from `default` up).
      //
      // Offsets put the dot's CENTER on the avatar circle's edge at the
      // bottom-right diagonal — half over the avatar, half off. Per pairing:
      // offset = r(1−1/√2) − d/2, rounded to the px grid, so small avatars
      // push out ~1px while lg/xl pull in — the corner of their bounding box
      // sits further outside the circle.
      size: {
        "2xs": "size-1.5 border -bottom-px -right-px",
        xs: "size-2 border -bottom-px -right-px",
        sm: "size-2 border -bottom-px -right-px",
        default: "size-2.5 border-2 -bottom-px -right-px",
        lg: "size-3 border-2 bottom-0 right-0",
        xl: "size-3.5 border-2 bottom-0.5 right-0.5",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

// Mobile variant — a SOLID phone silhouette in the status color: a filled
// rounded-rect body with a background-colored "screen" cutout, ringed in the
// page background for the same knockout the dot gets. Anchored on the dot's
// own per-size offsets — the silhouette is compact enough to share them.
// Portrait proportions per avatar size; arbitrary px classes are literal so
// Tailwind emits them.
const entityAvatarPresenceMobileVariants = cva(
  "pointer-events-none absolute z-10 border-background",
  {
    variants: {
      status: {
        online: "bg-success",
        away: "bg-warning",
        offline: "bg-muted-foreground",
      },
      size: {
        "2xs": "w-[6px] h-[9px] rounded-[2px] border -bottom-px -right-px",
        xs: "w-[7px] h-[10px] rounded-[2px] border -bottom-px -right-px",
        sm: "w-[7px] h-[11px] rounded-[2px] border -bottom-px -right-px",
        default: "w-[9px] h-[13px] rounded-[3px] border -bottom-px -right-px",
        lg: "w-[10px] h-[15px] rounded-[3px] border-2 bottom-0 right-0",
        xl: "w-[12px] h-[17px] rounded-[3px] border-2 bottom-0.5 right-0.5",
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

// The screen cutout, inset from the body so the status color reads as
// bezels: 1px sides, thicker chin at the bottom (the phone read). Keyed by
// the same avatar size as the body.
const MOBILE_SCREEN_CLASS: Record<EntityAvatarSize, string> = {
  "2xs": "left-px right-px top-px bottom-[2px] rounded-[1px]",
  xs: "left-px right-px top-px bottom-[2px] rounded-[1px]",
  sm: "left-px right-px top-[2px] bottom-[2px] rounded-[1px]",
  default: "left-px right-px top-[2px] bottom-[3px] rounded-[1px]",
  lg: "left-px right-px top-[2px] bottom-[3px] rounded-[1px]",
  xl: "left-[2px] right-[2px] top-[2px] bottom-[3px] rounded-[1px]",
};

export type EntityAvatarPresence = Omittable<{
  status: EntityAvatarPresenceStatus;
  /** Presence comes from a phone: render a smartphone glyph in the status
   *  color instead of the dot. */
  mobile?: boolean;
}>;

/**
 * Live-presence indicator overlapping the avatar's bottom-right corner —
 * green online, yellow away, grey offline. Web/desktop presence renders as a
 * dot ringed in the page background; phone-only presence (`mobile`) renders
 * a smartphone glyph in the same status color.
 *
 * Prefer `EntityAvatar`'s `presence` prop, which composes this for you (the
 * avatar root clips to its circle, so the prop renders the indicator as a
 * sibling inside a relative wrapper). Purely decorative to pointers; the
 * status is exposed to assistive tech as a label.
 */
export function EntityAvatarPresenceIndicator({
  status,
  size,
  mobile,
}: EntityAvatarPresence & { size?: EntityAvatarSize }): React.ReactElement {
  if (mobile) {
    return (
      <Box
        aria-label={`${PRESENCE_LABELS[status]} on mobile`}
        as="span"
        className={entityAvatarPresenceMobileVariants({ status, size })}
        role="img"
      >
        <Box
          as="span"
          className={cn(
            "absolute bg-background",
            MOBILE_SCREEN_CLASS[size ?? "default"],
          )}
        />
      </Box>
    );
  }
  return (
    <Box
      aria-label={PRESENCE_LABELS[status]}
      as="span"
      className={entityAvatarPresenceDotVariants({ status, size })}
      role="img"
    />
  );
}

type EntityAvatarState = { muted?: boolean; invited?: boolean };
const EntityAvatarStateContext = React.createContext<EntityAvatarState>({});
const EntityAvatarShapeContext = React.createContext<"circle" | "square">(
  "circle",
);

export type EntityAvatarProps = React.ComponentProps<typeof Avatar> &
  VariantProps<typeof entityAvatarVariants> &
  Omittable<{
    /** Suspended treatment — greyscale + dimmed image, gray fallback. */
    muted?: boolean;
    /** Invited treatment — dashed ring + reduced opacity. */
    invited?: boolean;
    /** Accessible label for the avatar. */
    alt?: string;
    /** Live presence indicator on the avatar's bottom-right corner. The
     *  avatar root clips to its circle, so the indicator renders as a
     *  sibling inside a relative wrapper rather than as a child. */
    presence?: EntityAvatarPresence | null;
  }>;

export function EntityAvatar({
  className,
  size,
  shape,
  muted,
  invited,
  alt,
  presence,
  children,
  ...props
}: EntityAvatarProps): React.ReactElement {
  const state = React.useMemo(
    () => ({ muted: !!muted, invited: !!invited }),
    [muted, invited],
  );
  const avatar = (
    <EntityAvatarShapeContext.Provider value={shape ?? "circle"}>
      <EntityAvatarStateContext.Provider value={state}>
        <Avatar
          aria-label={alt}
          className={cn(
            entityAvatarVariants({ size, shape, invited }),
            className,
          )}
          data-slot="entity-avatar"
          {...props}
        >
          {children}
        </Avatar>
      </EntityAvatarStateContext.Provider>
    </EntityAvatarShapeContext.Provider>
  );
  // Suspended/invited avatars suppress the dot — "online" is meaningless for
  // an account that cannot be signed in.
  if (!presence || muted || invited) return avatar;
  return (
    <Box as="span" className="relative inline-flex shrink-0">
      {avatar}
      <EntityAvatarPresenceIndicator
        mobile={presence.mobile ?? false}
        size={size ?? "default"}
        status={presence.status}
      />
    </Box>
  );
}

export function EntityAvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarImage>): React.ReactElement {
  const { muted, invited } = React.useContext(EntityAvatarStateContext);
  return (
    <AvatarImage
      className={cn(
        "aspect-square",
        muted && "grayscale brightness-[0.25]",
        invited && "grayscale opacity-40",
        className,
      )}
      {...props}
    />
  );
}

export function EntityAvatarFallback({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AvatarFallback>): React.ReactElement {
  const shape = React.useContext(EntityAvatarShapeContext);
  const { muted, invited } = React.useContext(EntityAvatarStateContext);
  const gray = muted || invited;
  return (
    <AvatarFallback
      className={cn(
        shape === "square" ? "rounded-md" : "rounded-full",
        // Text color goes on the element; descendant text inherits it.
        gray
          ? "bg-muted text-muted-foreground"
          : "bg-primary text-primary-foreground",
        invited && "opacity-40",
        className,
      )}
      {...props}
    >
      {children}
    </AvatarFallback>
  );
}
